import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useId, useRef, useState, type FormEvent, type ReactElement } from 'react';
import type { ConnectionState, CredentialField } from '@chatapp-inbox/shared';
import { saveConnection } from '../../api';
import { Button } from '../details/Button';
import { StatusRegion } from '../details/StatusRegion';
import { useStatusMessage } from '../details/use-status-message';
import { connectionErrorMessage } from './error-message';
import { Field } from './Field';
import { syncKey } from './keys';
import { foundSentence } from './lines-text';
import { TokenAccounts } from './TokenAccounts';
import { applyConnection } from './use-connection';
import {
  connectionPayload,
  validateConnection,
  type ConnectionValues,
  type Errors,
} from './validation';
import forms from './forms.module.css';
import section from './Section.module.css';
import styles from './ConnectionSection.module.css';

/** Frase da dica terminada em ponto (a do provedor pode vir sem), para somar a seguinte. */
const endSentence = (text: string): string => {
  const trimmed = text.trim();
  return /[.!?…:]$/.test(trimmed) ? trimmed : `${trimmed}.`;
};

/** Valores iniciais: os atuais dos campos comuns; os SEGREDOS começam sempre em branco (nunca voltam). */
function initialValues(connection: ConnectionState): ConnectionValues {
  return Object.fromEntries(
    connection.fields.map((field) => [
      field.key,
      field.secret ? '' : (connection.values[field.key] ?? ''),
    ]),
  );
}

/** Situação da conexão em palavra e em forma (selo cheio x tracejado), nunca só em cor. */
function StateLine({ connected }: { connected: boolean }): ReactElement {
  return (
    <p className={styles.state}>
      <span className={styles.term}>Situação</span>{' '}
      <span className={connected ? section.badgeOn : section.badgeOff}>
        {connected ? 'Conectado' : 'Não conectado'}
      </span>
    </p>
  );
}

/**
 * Conexão com o WhatsApp. O formulário é GERADO a partir dos campos que o provedor da instalação
 * declara (`connection.fields`): a mesma tela serve a qualquer provedor, e nenhum deles é citado.
 * Instalação que não pede nada mostra só a situação.
 *
 * Segredo: o campo é `type="password"` e começa vazio. Vazio MANTÉM o guardado (o valor nunca volta
 * do servidor): não vai no corpo, nem como `""`. Preenchido vai exatamente como digitado (sem
 * `trim`) e é esvaziado quando o servidor aceita.
 */
export function ConnectionSection({ connection }: { connection: ConnectionState }): ReactElement {
  const queryClient = useQueryClient();
  const headingId = useId();
  const status = useStatusMessage();
  const { fields, secretsSet, connected } = connection;
  const [values, setValues] = useState<ConnectionValues>(() => initialValues(connection));
  const [errors, setErrors] = useState<Errors<string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const inputs = useRef(new Map<string, HTMLInputElement>());

  const save = useMutation({
    // As variáveis (com os segredos) saem da memória do React Query assim que a mutação acaba.
    gcTime: 0,
    mutationFn: saveConnection,
    onSuccess: (saved) => {
      applyConnection(queryClient, saved);
      // Salvar pode iniciar a importação no servidor: a seção dela passa a acompanhá-la.
      if (saved.canSync) void queryClient.invalidateQueries({ queryKey: syncKey });
    },
  });
  const pending = save.isPending;

  if (fields.length === 0) {
    return (
      <section aria-labelledby={headingId} className={section.card}>
        <h3 id={headingId} className={section.cardTitle}>
          Conexão
        </h3>
        <p className={forms.hint}>
          Esta instalação não precisa de credenciais para falar com o WhatsApp.
        </p>
        <StateLine connected={connected} />
      </section>
    );
  }

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (pending) return;
    const found = validateConnection(fields, values, secretsSet);
    setErrors(found);
    setFormError(null);
    const first = fields.find((field) => found[field.key]);
    if (first) {
      inputs.current.get(first.key)?.focus();
      return;
    }
    save.mutate(connectionPayload(fields, values), {
      onSuccess: (saved) => {
        save.reset(); // solta as variáveis da mutação (com os segredos) em vez de esperar a tela sair
        setValues(initialValues(saved));
        status.announce(`Conexão salva. ${foundSentence(saved.lines.length)}`);
      },
      onError: (error) => {
        setFormError(
          connectionErrorMessage(error, 'Não foi possível salvar a conexão. Tente de novo.'),
        );
      },
    });
  };

  return (
    <section aria-labelledby={headingId} className={section.card}>
      <h3 id={headingId} className={section.cardTitle}>
        Conexão
      </h3>
      <StateLine connected={connected} />
      <p className={forms.hint}>
        {connected
          ? 'Para trocar as credenciais, preencha de novo os campos que mudaram. As credenciais guardadas ficam criptografadas no servidor e nunca são exibidas.'
          : 'Preencha os dados abaixo e salve para conectar. Sem a conexão, não há como receber nem enviar mensagens. As credenciais ficam guardadas criptografadas no servidor e nunca são exibidas de novo.'}
      </p>
      <form className={forms.form} noValidate aria-busy={pending || undefined} onSubmit={submit}>
        {fields.map((field) => (
          <ConnectionField
            key={field.key}
            field={field}
            kept={field.secret && secretsSet.includes(field.key)}
            value={values[field.key] ?? ''}
            error={errors[field.key]}
            readOnly={pending}
            onChange={(value) => setValues((current) => ({ ...current, [field.key]: value }))}
            register={(element) => {
              if (element) inputs.current.set(field.key, element);
              else inputs.current.delete(field.key);
            }}
          />
        ))}
        {formError && (
          <p role="alert" className={forms.alert}>
            {formError}
          </p>
        )}
        <div className={forms.actions}>
          <Button type="submit" variant="primary" busy={pending}>
            {pending ? 'Salvando…' : connected ? 'Salvar alterações' : 'Salvar e conectar'}
          </Button>
        </div>
        <StatusRegion message={status.message} />
      </form>
      {/* Quando a Meta diz que não achou a conta, a lista do próprio token mostra o ID certo. */}
      {connection.canListAccounts && <TokenAccounts />}
    </section>
  );
}

interface ConnectionFieldProps {
  field: CredentialField;
  /** Segredo que já está guardado: em branco significa "manter". */
  kept: boolean;
  value: string;
  error: string | undefined;
  readOnly: boolean;
  onChange: (value: string) => void;
  register: (element: HTMLInputElement | null) => void;
}

function ConnectionField({
  field,
  kept,
  value,
  error,
  readOnly,
  onChange,
  register,
}: ConnectionFieldProps): ReactElement {
  const hint =
    [
      field.hint ? endSentence(field.hint) : null,
      kept ? 'Já guardado. Deixe em branco para manter.' : null,
    ]
      .filter(Boolean)
      .join(' ') || undefined;
  return (
    <Field
      ref={register}
      label={field.required ? field.label : `${field.label} (opcional)`}
      hint={hint}
      type={field.secret ? 'password' : 'text'}
      // `new-password`: o gerenciador de senhas não deve preencher a senha DE LOGIN do usuário aqui.
      autoComplete={field.secret ? 'new-password' : 'off'}
      autoCapitalize="none"
      spellCheck={false}
      aria-required={field.required && !kept ? true : undefined}
      value={value}
      readOnly={readOnly}
      error={error}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}
